<script>
    // App-styled toast for svelte-toasts' <ToastContainer let:data>. Dark surface,
    // coloured icon badge per type, thin countdown bar. Replaces the stock FlatToast.
    import { onMount } from "svelte";
    import { tweened } from "svelte/motion";
    import { linear } from "svelte/easing";
    import Icon from "@iconify/svelte";

    export let data = {};

    const ICONS = {
        success: "solar:check-circle-bold",
        error: "solar:danger-circle-bold",
        warning: "solar:danger-triangle-bold",
        info: "solar:info-circle-bold",
    };
    $: type = ICONS[data.type] ? data.type : "info";

    const progress = tweened(1, { duration: data.duration, easing: linear });
    onMount(() => {
        if (data.duration > 0) progress.set(0);
    });

    const close = (e) => {
        e.stopPropagation();
        data.remove?.();
        if (typeof data.onRemove === "function") data.onRemove();
    };
    const click = () => {
        if (typeof data.onClick === "function") data.onClick();
    };
</script>

<div class="jt jt--{type}" role="alert" aria-live="assertive" aria-atomic="true" on:click={click}>
    <span class="jt-icon"><Icon icon={ICONS[type]} width="20" /></span>
    <div class="jt-body">
        {#if data.title}<p class="jt-title">{data.title}</p>{/if}
        {#if data.description}<p class="jt-desc">{data.description}</p>{/if}
    </div>
    <button class="jt-close" type="button" aria-label="Dismiss" on:click={close}>
        <Icon icon="material-symbols:close-rounded" width="16" />
    </button>
    {#if data.duration > 0}
        <span class="jt-bar" style="transform: scaleX({$progress})" />
    {/if}
</div>

<style>
    .jt {
        --c: #5be1e6;
        --c-soft: rgba(91, 225, 230, 0.14);
        position: relative;
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        min-width: 280px;
        padding: 12px 10px 14px 12px;
        overflow: hidden;
        border-radius: 14px;
        border: 1px solid rgba(244, 243, 236, 0.1);
        background: rgba(20, 20, 20, 0.92);
        backdrop-filter: blur(14px);
        -webkit-backdrop-filter: blur(14px);
        box-shadow: 0 18px 40px -18px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.04);
        color: var(--jh-text, #f4f3ec);
        font-family: var(--jh-font, inherit);
        pointer-events: auto;
        cursor: default;
    }
    .jt--success { --c: #b6ff3a; --c-soft: rgba(182, 255, 58, 0.14); }
    .jt--error   { --c: #ff5c5c; --c-soft: rgba(255, 92, 92, 0.14); }
    .jt--warning { --c: #ffc53d; --c-soft: rgba(255, 197, 61, 0.14); }

    .jt-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 34px;
        height: 34px;
        border-radius: 10px;
        background: var(--c-soft);
        color: var(--c);
    }

    .jt-body {
        flex: 1 1 auto;
        min-width: 0;
    }
    .jt-title {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.35;
        overflow-wrap: anywhere;
    }
    .jt-desc {
        margin: 2px 0 0;
        font-size: 13px;
        line-height: 1.35;
        color: var(--jh-muted, #a5a5a1);
    }

    .jt-close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 28px;
        height: 28px;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: var(--jh-muted, #a5a5a1);
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;
    }
    .jt-close:hover {
        background: rgba(244, 243, 236, 0.08);
        color: var(--jh-text, #f4f3ec);
    }

    .jt-bar {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2px;
        background: var(--c);
        opacity: 0.7;
        transform-origin: left center;
    }
</style>
